import { OPERATORS } from 'src/app/services/constants';
import { CollectionPopupComponent } from '../../../shared/index';
import { WarningPopup } from '../../../shared/warning-popup/warning-popup';
import { PendingPlaybackRef } from '../shared/contracts/playback-controller';

import { VoyageActiveVessel } from './toolbar/track.controller';
import { buildClusterPopup, buildVesselPopup } from './vessel-popup';
import { VOYAGE_DEFAULT_TRACK_COLOR } from './panels/track-history/voyage-track-history.model';
import { formatVoyageDate } from './panels/refresh-history/voyage-refresh-history.model';
// One import left from the credits model, and it is the wallet WRITE path:
// `applyCreditSpend` still lives here (it reconciles the sensor document after
// a spend, which is the provider's own bookkeeping). Every credit READ — price,
// balance, refusal, spending-closed — now goes through `ctx.spend`, the D1
// gate, which is why the other four imports left with the lookup in phase 9.
import { voyageCreditsRemaining } from './voyage-credits.model';
import { LOCATED_COLOR } from '../shared/models/located.model';
import { TargetController } from '../shared/map-core/target.controller';
import { SpendBroadcast } from '../shared/map-core/spend-broadcast';
import { SnapshotController } from '../shared/map-core/snapshot.controller';
import { DisplayController } from '../shared/map-core/display.controller';
import { SelectionController } from '../shared/map-core/selection.controller';
import { PopupHost } from '../shared/map-core/popup.host';
import { LocatedStore } from '../shared/map-core/located.store';
import { LocatedLayer } from '../shared/map-core/located.layer';
import { LocatedVesselEntry, LocatedVesselRecord, PlaybackStep, VesselSearchHit } from '../shared/contracts/vessel.types';
import { VoyageCardAction, VoyagePopupActions } from './vessel-popup.types';
import { AisMapFeature } from '../shared/contracts/ais-map.feature';
import { PortCallsFeature } from './features/port-calls.feature';
import { NearbyFeature } from './features/nearby.feature';
import { SearchFeature } from './features/search.feature';
import { PoiNearbyFeature } from './features/poi-nearby.feature';
import { TrackFeature } from './features/track.feature';
import { LookupFeature } from './features/lookup.feature';
import {
  AisMapContext,
  DisplayPort,
  LocatedPort,
  PopupPort,
  SelectionPort,
  SnapshotPort,
  TargetPort,
} from '../shared/contracts/ais-map.context';
import { SpendGate } from '../shared/contracts/spend-gate';
import { VrSpendGateway } from './gateways/vr-spend.gateway';
import {
  vrArchive,
  vrArchiveKey,
  vrCaptureId,
  vrCaptureLabel,
  vrCaptureTime,
  vrLoadCapture,
} from './gateways/vr-snapshot-archive.gateway';
import {
  vrIsSaved,
  vrRemoveVessel,
  vrSaveVessel,
} from './gateways/vr-saved-vessels.gateway';
import { vrToLocatedRecord } from './gateways/vr-lookup.gateway';
import {
  actionCredits,
  VOYAGE_POI_RADIUS_KM,
} from 'src/app/services/operators/voyageradar-credit-actions';

import { vrHost, VoyageRadarMapHost } from './vr-host';
import { AisMapHost } from '../shared/contracts/ais-map.host';

/**
 * Owns everything Voyage Radar draws on the map. Six areas, in the order the
 * `── ──` section markers below run — this list is the file's table of contents:
 *
 *   THE TASKED TARGET — the yellow AOI ring, or the POI reticle and its queried
 *   radius disc. Its lifecycle is the sensor CHECKBOX, not the data.
 *
 *   AOI SNAPSHOTS — the live vessel layer, frame playback over saved refreshes,
 *   and the toolbar's Refresh button.
 *
 *   THE DISPLAY PASS — ship-type filter, count-bubble clustering and name-label
 *   thinning. One pass; each stage decides what the next one sees.
 *
 *   TRACK MODE — the selected vessel, its halo, and the replay of a single
 *   vessel's path (a paid fetch from the operator, or a saved track from S3).
 *
 *   LOCATED VESSELS — hulls found by worldwide search or a by-MMSI lookup, on
 *   their own amber layer above the snapshot so no display rule can hide one the
 *   user explicitly asked for.
 *
 *   PAID BY-MMSI QUESTIONS — the vessel lookup, port-call history and nearby
 *   search, plus the wallet and the credit lines pushed to every priced control.
 *
 * They share one map, one viewport, one set of vessel colours and one wallet,
 * which is why they live together. AOI snapshots and track mode are also
 * mutually exclusive on screen.
 *
 * Extracted from LeafletNewcollectionsComponent to keep the parent lean, the
 * same way WeatherExMapController is. It has since grown well past that
 * controller's size: the last three areas above arrived as separate features
 * over ~2 weeks, each one reaching a `this.` away for the map and the wallet
 * that were already here. See the toolbar's own plugin registry
 * (`TOOLBAR_CONTROLS` / `SENSOR_TOOLBAR_CONFIGS`) for the pattern this half of
 * the feature has not yet been given.
 *
 * Lifecycle: instantiated once per component instance via
 *   `readonly voyage = new VoyageMapController(this as unknown as AisMapHost);`
 * then `attach()` from the constructor to wire the toolbar callbacks.
 *
 * Division of labour with the two toolbar controllers: they own the clock and
 * the button state, this owns Leaflet. Frames arrive here ~10x/s, so the render
 * paths mutate existing layers rather than recreating them.
 */
export class VoyageMapController implements AisMapContext {
  // ── The context (§4.2) ──────────────────────────────────────────────────────
  // The facade IS the `AisMapContext` handed to every extracted core module.
  // Each port below is served by an adapter literal over this class's own
  // methods until its module lands in its own phase-4 commit — at which point
  // the member becomes the module instance and the adapter goes. The seam is
  // identical either way; only what stands behind it changes.
  /**
   * The display pass — extracted to `ais/shared/map-core/display.controller.ts`
   * (phase 4). The track-mode dim state rides in as thunks: both facts belong
   * to the track feature, which keeps them on this facade until phase 6.
   */
  private readonly _display = new DisplayController(this, {
    focusMmsi: () => this._trackFeature.layers.focusMmsi,
    tracedMmsi: () => this._trackFeature.layers.tracedMmsi,
  });
  readonly display: DisplayPort = this._display;
  /**
   * The selection — extracted to `ais/shared/map-core/selection.controller.ts`
   * (phase 4). Each injected callback is a seam to code still on this facade:
   * the saved-track list and the credit push (phase 5's feature fan-out), the
   * track teardown (phase 6's track feature), and the provider's default trail
   * colour (shared code is told the constant, never taught where it lives).
   */
  private readonly _selection = new SelectionController(this, {
    defaultTrackColor: VOYAGE_DEFAULT_TRACK_COLOR,
    savedTracksFor: (mmsi) => this._trackFeature.savedTracksFor(mmsi),
    syncCreditLines: () => this.syncCreditLines(),
    clearTrackLayers: () => this._trackFeature.layers.clearTrackLayers(),
  });
  readonly selection: SelectionPort = this._selection;
  /**
   * Located vessels — extracted to `ais/shared/map-core/located.store.ts`
   * (records · keep/remove · panel rows) and `located.layer.ts` (amber
   * markers · twins · labels), phase 4. Two halves, one seam each way — the
   * layer paints out of the store's entry map, the store exits markers
   * through the layer — and BOTH wires live here, because siblings never
   * import each other (rule R2). The port literal below is their composition
   * point: peers see one `located`.
   */
  private readonly _locatedStore = new LocatedStore(this, {
    removeMarker: (marker) => this._locatedLayer.removeMarker(marker),
    // Saved-vessel persistence is Voyage Radar's (D8) — the shared store keeps
    // the record shape and the panel rows, and is told the three acts.
    isSavedRecord: (mmsi) => vrIsSaved(this.sensor, mmsi),
    persist: (record) => vrSaveVessel(this.host.apiService, this.saveScope, record),
    forget: (mmsi, wasKept) =>
      vrRemoveVessel(this.host.apiService, this.saveScope, mmsi, wasKept),
  });
  private readonly _locatedLayer = new LocatedLayer(this, {
    vessels: () => this._locatedStore.vessels,
    onMarkerClick: (mmsi) => this.selectLocated(mmsi, { openPopup: true }),
  });
  readonly located: LocatedPort = {
    syncLocatedTwins: () => this._locatedLayer.syncLocatedTwins(),
    pushLocatedState: () => this._locatedStore.pushLocatedState(),
    isLocatedOnMap: (mmsi) => this._locatedStore.isLocatedOnMap(mmsi),
    // Grown in phase 9 for the lookup feature — the store's reads and writes,
    // the layer's draw and card. Still one `located` to its consumers: that
    // the halves are two objects is this facade's problem, not theirs.
    isSaved: (mmsi) => this._locatedStore.isSaved(mmsi),
    recordFor: (mmsi) => this._locatedStore.recordFor(mmsi),
    keepVessel: (record) => this._locatedStore.keepVessel(record),
    upsertMarker: (record) => this._locatedLayer.upsertLocatedMarker(record),
    openInfo: (mmsi) => this._locatedLayer.openLocatedInfo(mmsi),
  };
  /**
   * The vessel card's host — extracted to `ais/shared/map-core/popup.host.ts`
   * (phase 4). The card builders ride in as callbacks because the markup
   * lives with the provider until phase 8; the action reads and dispatches
   * are seams to features still on this facade, replaced by the features'
   * own `popupActions()` contributions in phase 5 (decision D6 is taken
   * there, against those real implementers).
   */
  private readonly _popup = new PopupHost(this, {
    buildVesselCard: (properties, actions) => buildVesselPopup(properties, actions),
    buildClusterCard: (vessels) => buildClusterPopup(vessels),
    providerActions: (mmsi) => this.voyageCardActions(mmsi),
    isSaved: (mmsi) => this.isSaved(mmsi),
    isLocatedOnMap: (mmsi) => this.isLocatedOnMap(mmsi),
    dispatchCardAction: (action, mmsi) => this.dispatchCardAction(action, mmsi),
    saveVessel: (mmsi) => this.saveSnapshotVessel(mmsi),
    takeOffMap: (mmsi) => this.takeLocatedOffMap(mmsi),
  });
  readonly popup: PopupPort = this._popup;

  // ── AOI snapshots ───────────────────────────────────────────────────────────
  /**
   * The live layer, playback frames and toolbar refresh — EXTRACTED to
   * `ais/shared/map-core/snapshot.controller.ts` (phase 4). The provider bits
   * ride in as callbacks: the pending-row date style (its formatter lives with
   * the refresh-history panel model, which shared code must not import) and
   * the search-context push, which becomes the search feature's `onLiveLayer`
   * hook once features register (phase 5).
   */
  private readonly _snapshot = new SnapshotController(this, {
    formatDate: (timeMs) => formatVoyageDate(timeMs),
    pushSearchContext: () => this.pushSearchContext(),
    // Which payload shape the host's shared converter reads a frame as.
    // The literal, NOT `OPERATORS.VOYAGERADAR.toUpperCase()`: the converter
    // switches on its own vocabulary ('overlay' · 'CLYDE' · 'SPIRE' ·
    // 'VOYAGERADAR'), which matches the operator key here by coincidence, and
    // deriving one from the other would break silently the day either moves.
    geoJsonType: 'VOYAGERADAR',
    // The saved-capture archive is Voyage Radar's (D8) — the shared module is
    // told how to read it, never what it looks like.
    archive: () => vrArchive(this.sensor),
    captureTime: (record) => vrCaptureTime(record),
    captureLabel: (record) => vrCaptureLabel(record),
    captureId: (record) => vrCaptureId(record),
    archiveKey: () => vrArchiveKey(this.sensor),
    loadCapture: (record) =>
      vrLoadCapture(
        this.host.apiService,
        this.host.httpClient,
        { sensorId: this.sensor?.id, taskId: this.host.collection?.taskId },
        record,
      ),
  });
  /** The same instance, as the narrow port the other core modules read. */
  readonly snapshot: SnapshotPort = this._snapshot;

  // The snapshot state lives on the module; these accessors keep the host
  // component, the templates and the untouched halves of this file reading
  // and writing the one copy under the names they always used.
  //
  // Three, not four: `playbackLayer` had its accessor pair deleted in phase 7.
  // Nothing outside `ais/` ever read it, and inside `ais/` the frame layer is
  // reached through `SnapshotPort` — so the facade's copy was a second door
  // onto a room that already had one. That is the whole test this phase
  // applies: a delegate earns its place by having a caller the port cannot
  // serve, which for these three is the host component and the template.
  /** True while Voyage Radar vessels are on the map — gates the live toolbar. */
  get dataActive(): boolean {
    return this._snapshot.dataActive;
  }
  set dataActive(v: boolean) {
    this._snapshot.dataActive = v;
  }
  /** The layer currently holding the LIVE vessels — see the module's field. */
  get liveLayer(): any {
    return this._snapshot.liveLayer;
  }
  set liveLayer(v: any) {
    this._snapshot.liveLayer = v;
  }
  /** The features currently visible (live snapshot or playback frame). */
  get currentFeatures(): any[] {
    return this._snapshot.currentFeatures;
  }
  set currentFeatures(v: any[]) {
    this._snapshot.currentFeatures = v;
  }
  /**
   * The tasked target — EXTRACTED to `ais/shared/map-core/target.controller.ts`
   * (phase 2). The lifecycle reasoning lives on the class there; this facade
   * keeps delegates for its public surface and hands the provider-specific
   * answers in as callbacks: which scene is the target (`targetScene`, which
   * knows OPERATORS.VOYAGERADAR), the tasked POI radius, and the reticle
   * builder (the POI feature's — its popup rides the marker).
   */
  private readonly _target = new TargetController(this.host, {
    ownScene: (collection) => this.targetScene(collection),
    defaultPoiRadiusKm: VOYAGE_POI_RADIUS_KM,
    // The reticle's LOOK (and the popup it carries) belong to the POI
    // feature; the marker itself belongs to the target. Deferred, so the
    // feature declared below is resolved at call time, not at field init.
    buildReticle: (lat, lng) => this._poiFeature.buildPoiReticle(lat, lng),
  });
  /** The same instance, as the narrow port a feature may fill but not own. */
  readonly target: TargetPort = this._target;

  /**
   * Voyage Radar's answer to "may I spend, and what does it cost?" — a thunk
   * over the open sensor, so the gate follows the collection (D1, phase 2).
   * First consumer of the phase-0 seam.
   */
  private readonly _spend = new VrSpendGateway(() => this.sensor);
  /**
   * The pluggable features (phase 5, §4.3) — each one file under
   * `ais/voyage-radar/features/`, handed the same context the core modules
   * get, with its remaining facade couplings injected as documented options
   * (each option names the phase that dissolves it).
   *
   * PROVIDER-OWNED (decision D7): port calls, nearby and worldwide search are
   * Voyage Radar capabilities, not platform ones, so the files live in this
   * provider's folder and call its endpoints directly. What is SHARED is the
   * `AisMapFeature` contract they implement, the map core they drive and the
   * drawer UI they fill — a provider without these questions mounts no
   * feature and lists no control, and nothing renders. Adding a priced
   * by-MMSI drawer feature is one file there, one entry here.
   */
  private readonly _portCallsFeature = new PortCallsFeature(this, {
    syncCreditLines: () => this.syncCreditLines(),
  });
  private readonly _nearbyFeature = new NearbyFeature(this, {
    snapshotFixFor: (mmsi) => this.snapshotFixFor(mmsi),
    pickRowVessel: (mmsi, onSettled) => this.pickNearbyRowVessel(mmsi, onSettled),
    syncCreditLines: () => this.syncCreditLines(),
  });
  private readonly _searchFeature = new SearchFeature(this, {
    pickLocal: (hit) => this.locateLoadedVessel(hit),
    pickWorldwide: (row, q) => this.locateSearchResult(row, q),
    syncCreditLines: () => this.syncCreditLines(),
  });
  private readonly _poiFeature = new PoiNearbyFeature(this, {
    lookupCredits: () => this.lookupCredits,
    locateHistoryRecord: (rec) => this.locateHistoryRecord(rec),
    selectLocated: (mmsi, opts) => this.selectLocated(mmsi, opts),
    refreshVesselLookup: (mmsi, opts) => this.refreshVesselLookup(mmsi, opts),
  });
  private readonly _trackFeature = new TrackFeature(this, {
    applyCreditSpend: (res, sensorId, cost) => this.applyCreditSpend(res, sensorId, cost),
    syncCreditLines: () => this.syncCreditLines(),
  });
  /**
   * The by-MMSI lookup and the free save beside it (phase 9). Two options only,
   * and both are cross-feature coordination — everything else it needs, it now
   * asks the located and popup PORTS for, which is why those grew instead.
   */
  private readonly _lookupFeature = new LookupFeature(this, {
    selectLocated: (mmsi, opts) => this.selectLocated(mmsi, opts),
    syncCreditLines: () => this.syncCreditLines(),
  });
  /** Order is presentation-neutral: wiring is per-controller, popup keys disjoint. */
  private readonly _features: AisMapFeature[] = [
    this._portCallsFeature,
    this._nearbyFeature,
    this._searchFeature,
    this._poiFeature,
    this._trackFeature,
    this._lookupFeature,
  ];

  /**
   * The one call that keeps every priced drawer's numbers honest — the
   * phase-2 broadcast, which phase 5 turned into the feature fan-out (§4.3)
   * and phase 6 completed: the gate is read once, and EVERY price line is now
   * a feature's own `syncCredits`. Nothing is hardcoded here any more.
   */
  private readonly _spendBroadcast = new SpendBroadcast(this._spend, () => this._features);

  // `loadedSteps` and `setVesselPopupUpdating` were delegates until phase 9
  // took their last callers into the lookup feature. Both are reachable
  // through the ports that always served them (`ctx.snapshot.loadedSteps`,
  // `ctx.popup.setVesselPopupUpdating`), so by the phase-7 test — a delegate
  // earns its place by having a caller the port cannot serve — they had
  // stopped earning it. Caught by re-running phase 7's census after phase 9,
  // which is the point of a census being a script rather than a reading.

  // ── Track mode ──────────────────────────────────────────────────────────────
  /** The vessel the user has selected (null = none) — the selection module's. */
  get active(): VoyageActiveVessel | null {
    return this._selection.active;
  }
  set active(v: VoyageActiveVessel | null) {
    this._selection.active = v;
  }
  // Track mode's layers, hover readout, view bookkeeping and fleet-dim state
  // all live on `TrackLayers`, owned by the phase-6 `TrackFeature` above.

  /** MMSI → live name/colour for the drawn snapshot — the module's index. */
  get liveVessels(): Map<string, { name?: string | null; color?: string | null }> {
    return this._snapshot.liveVessels;
  }

  // ── Located vessels (worldwide search) ──────────────────────────────────────
  // The located entry map and its Leaflet layer live on the phase-4 modules
  // above (`_locatedStore` / `_locatedLayer`); this accessor keeps the
  // remaining feature code (pickers · search · lookup — phase 5) reading the
  // one copy under the name it always used.
  private get _locatedVessels(): Map<string, LocatedVesselEntry> {
    return this._locatedStore.vessels;
  }

  // The port-call and nearby in-flight guards moved into their features
  // (phase 5) — each is one purchase's double-click guard, owned by the
  // feature that spends it.

  // The open vessel card, the hull it describes and the container click
  // delegate all live on the phase-4 `PopupHost` above (`_popup`).

  // The stale-fix threshold moved to `ais/shared/models/located.model.ts`
  // (LOCATED_STALE_FIX_MS) with the glyph rules that read it — phase 1.
  // ── Clustering + labels ─────────────────────────────────────────────────────
  // The whole display pass — filter → clusters → labels, the bubble pool and
  // the viewport listener — lives on the phase-4 `DisplayController` above.

  /**
   * `readonly`, not `private`: the facade implements `AisMapContext`, whose
   * `host` member is how the extracted modules reach the component surface.
   */
  constructor(readonly host: AisMapHost) {}

  /**
   * The host as VOYAGE RADAR sees it — the same object, with its six toolbar
   * members widened back to this provider's concrete controllers. The shared
   * contract narrows them so `ais/shared/` imports no provider class (D7
   * amendment); this facade is provider code and drives the full surface.
   */
  private get vr(): VoyageRadarMapHost {
    return vrHost(this.host);
  }

  /**
   * Wires both toolbar controllers' callbacks. Called once from the host
   * constructor.
   */
  attach(): void {
    const playback = this.vr.playback;

    playback.onLoadPlaybackSteps = (from, to) => this.loadPlaybackSteps(from, to);
    // Answers on the click, off the same window the load will walk, so the
    // pending row can say how many snapshots are coming before any of them are.
    playback.onDescribePlayback = (from, to) => this.describePlayback(from, to);
    // Whether the map's Playback button draws itself at all. A live read rather
    // than a pushed flag: the archive hangs off the sensor document, which the
    // host appends to from every refresh path.
    playback.onCanPlayHistory = () => this.canPlayHistory;
    playback.onRenderStep = (step) => this.renderStep(step);
    playback.onTypeFilterChange = () => this.applyTypeFilterToLive();
    // The T control on the live bar. Goes through the same pass as the filter
    // because a label may only appear on a hull that is actually drawn — and
    // what is drawn is settled by that pass and the clustering below it. In
    // playback the frame re-render carries the labels instead.
    playback.onLabelsToggle = () => {
      this.applyTypeFilterToLive();
      // Located markers obey the SAME toggle as the fleet — one labels
      // control, not a second per-vessel one (which shipped and was
      // immediately read as a duplicate).
      this.syncLocatedLabels();
    };
    // Grouping decides only which of the markers already on the map are drawn,
    // so it repaints them in place — no filter re-run, no frame rebuild, and it
    // works the same in playback as it does live.
    playback.onClustersToggle = () => this.applyVesselDisplay(this.displayLayer);
    // Toolbar Refresh → pull a fresh AOI snapshot (spends one refresh entitlement).
    playback.onRefresh = () => this.refresh();
    // "Playback history" with no saved snapshots in range → tell the user.
    playback.onHistoryEmpty = () =>
      this.host.dialog.open(WarningPopup, {
        width: '40%',
        data: {
          title: 'No Playback Available',
          message:
            'There are no saved refreshes to play back. Refresh the area at least twice to build a timeline.',
        },
      });
    // Snapshots existed and none of them arrived. A different accident from the
    // one above and a different thing to do about it — advising a user with 200
    // saved refreshes to go and make some more is why these were split.
    playback.onHistoryFailed = () =>
      this.host.dialog.open(WarningPopup, {
        width: '40%',
        data: {
          title: 'Playback Unavailable',
          message:
            'Those saved refreshes could not be loaded. Please check your connection and try again.',
        },
      });
    // Closing playback from the toolbar X resets the Timeseries toggle so its
    // visual state stays in sync with the (now-hidden) toolbar.
    playback.onExit = () => {
      this.host.timeseries = false;
      this.host.sliderView = false;
      this.restoreLiveLayer();
    };

    // The search-history drawer's per-row actions — same callback pattern.
    const vessel = this.vr.locatedVessels;
    vessel.onCenterVessel = (mmsi) => this.centerLocatedVessel(mmsi);
    vessel.onTakeOffMap = (mmsi) => this.takeLocatedOffMap(mmsi);
    // Inherited from the panel's ON MAP summary cards when those were removed;
    // this was the only act on them without a home elsewhere.
    vessel.onClearAll = () => this.takeAllLocatedOffMap();

    // The pluggable features wire their own drawers — port calls, nearby and
    // search each own the callbacks they answer (phase 5, §4.3). Same order
    // the three wiring blocks always ran in: ports → nearby → search.
    for (const f of this._features) f.attach();

    this.syncCreditLines();
    this.pushSearchContext();
  }

  /**
   * Task + sensor, the scope every saved-vessel write is filed under. One
   * getter because the two calls must never disagree about which document
   * they are editing, and `sensor_uuid ?? id` is the provider's own precedence.
   */
  private get saveScope(): { taskid: any; sensorId: any; sensor: any } {
    const sensor = this.sensor;
    return {
      taskid: this.host.collection?.taskId,
      sensorId: sensor?.sensor_uuid ?? sensor?.id,
      sensor,
    };
  }

  /** The open collection's task id — the search component's request context. */
  get taskId(): string | null {
    return this.host.collection?.taskId ?? null;
  }

  /** The first Voyage Radar sensor on the open collection, or null. */
  get sensor(): any | null {
    return (
      this.host.collection?.sensors?.find(
        (s: any) => s?.operatorKey === OPERATORS.VOYAGERADAR
      ) ?? null
    );
  }

  /** Voyage Radar's SpendGate — the context's D1 seam (`AisMapContext.spend`). */
  get spend(): SpendGate {
    return this._spend;
  }

  /**
   * True only when EVERY sensor in the collection is Voyage Radar.
   *
   * Deliberately not "contains a VR sensor": Voyage Radar replays through its
   * own track/playback toolbar rather than the shared Timeseries control, but in
   * a mixed task that control still belongs to the Spire/ClydeSpace/optical
   * sensor sitting next to it.
   */
  get isOnlyCollection(): boolean {
    const sensors = this.host.collection?.sensors ?? [];
    return (
      sensors.length > 0 &&
      sensors.every((s: any) => s?.operatorKey === OPERATORS.VOYAGERADAR)
    );
  }

  // ── The tasked AOI ──────────────────────────────────────────────────────────
  /**
   * The tasked area's bounds, read from a Voyage Radar sensor's scene footprint,
   * or null when the task carries no usable one.
   *
   * The single source for "where is this task": the zoom-to-task button, the
   * load-time framing below, and the nominatim guard in `loadUserDetails` — which
   * reads it to decide whether the country-level pan would be overwriting a view
   * that already means something — all go through here.
   *
   * Takes the collection as an argument because the load path calls it before
   * `host.collection` has been assigned; falls back to the open one otherwise.
   */
  /**
   * The scene carrying the task's target geometry.
   *
   * Shared by `aoiBounds` and `showTarget` so the outline and the framing can
   * never disagree about which scene the target is: one task, one target.
   *
   * Takes the collection as an argument because the load path calls it before
   * `host.collection` has been assigned; falls back to the open one otherwise.
   */
  private targetScene(collection?: any): any | null {
    const sensors = (collection ?? this.host.collection)?.sensors ?? [];
    const sensor = sensors.find((s: any) => s?.operatorKey === OPERATORS.VOYAGERADAR);
    return (
      sensor?.scenes?.find(
        (sc: any) =>
          sc?.footprint?.coordinates?.[0]?.length || sc?.footprint?.type === 'Point',
      ) ?? sensor?.scenes?.[0] ?? null
    );
  }

  /**
   * Where this task is, off the task data — delegates to the extracted
   * `TargetController` (phase 2, ais/shared/map-core), which carries the full
   * reasoning. Kept on the facade because five host call sites and the spec
   * drive it here.
   */
  aoiBounds(collection?: any): any | null {
    return this._target.aoiBounds(collection);
  }

  /** Frames the task's AOI. False when there is nothing to frame. */
  showAoi(collection?: any): boolean {
    return this._target.showAoi(collection);
  }

  /** Puts the tasked target up (ring or reticle+disc); see `TargetController.showTarget`. */
  showTarget(opts: { fit?: boolean; collection?: any; scene?: any } = {}): boolean {
    return this._target.showTarget(opts);
  }

  /** The DRAWN target's bounds — `aoiBounds` answers off the data instead. */
  targetBounds(): any | null {
    return this._target.targetBounds();
  }

  /** Takes the target off the map — the checkbox's other end. */
  clearTarget(): void {
    this._target.clearTarget();
  }

  // ── AOI snapshots — extracted to SnapshotController (phase 4) ───────────────
  /** Two or more replayable snapshots exist — the Playback control's gate. */
  get canPlayHistory(): boolean {
    return this._snapshot.canPlayHistory;
  }

  /** What the pending ON MAP row says while a replay's frames are in the air. */
  describePlayback(fromIso: string, toIso: string): PendingPlaybackRef | null {
    return this._snapshot.describePlayback(fromIso, toIso);
  }

  /** Loads the refresh-history snapshots in [from, to] as playback frames. */
  loadPlaybackSteps(fromIso: string, toIso: string): Promise<PlaybackStep[]> {
    return this._snapshot.loadPlaybackSteps(fromIso, toIso);
  }

  /** Renders one playback frame; null clears it (close / resume-live). */
  renderStep(step: PlaybackStep | null): void {
    this._snapshot.renderStep(step);
  }

  /** Puts back the live layer that entering playback hid. No-op otherwise. */
  restoreLiveLayer(): void {
    this._snapshot.restoreLiveLayer();
  }

  // ── The display pass — extracted to DisplayController (phase 4) ─────────────
  /** Re-applies the filter to the LIVE view by toggling visibility (I9). */
  applyTypeFilterToLive(): void {
    this._display.applyTypeFilterToLive();
  }

  /** Takes every count bubble off the map. */
  clearVesselClusters(): void {
    this._display.clearVesselClusters();
  }

  /** The full pipeline over one layer: clusters → labels → halo → twins. */
  private applyVesselDisplay(layer: any): void {
    this._display.applyVesselDisplay(layer);
  }

  /** Whichever layer the clusters and labels belong to right now. */
  private get displayLayer(): any {
    return this._display.displayLayer;
  }

  /** Stops recomputing on view changes (nothing left to recompute). */
  private stopViewportRepaint(): void {
    this._display.stopViewportRepaint();
  }

  /** Host entry: live vessels landed — adopt the layer, seed types, re-filter. */
  adoptLiveLayer(layer: any, features: any[]): void {
    this._snapshot.adoptLiveLayer(layer, features);
  }

  /** Drops the live-layer bookkeeping without touching the map itself. */
  releaseLiveLayer(keepLayerRef = false): void {
    this._snapshot.releaseLiveLayer(keepLayerRef);
  }

  // ── Selection lookups — extracted to SelectionController (phase 4) ──────────
  /** The live layer's marker for an MMSI, or null when that hull is not drawn. */
  private liveMarkerFor(mmsi: string): any | null {
    return this._selection.liveMarkerFor(mmsi);
  }

  // ── Toolbar refresh — extracted to SnapshotController (phase 4) ─────────────
  /** Toolbar Refresh → pull a fresh AOI snapshot for the first VR sensor. */
  refresh(): Promise<void> {
    return this._snapshot.refresh();
  }

  /** Pull a fresh AOI snapshot for a specific Voyage Radar sensor. */
  refreshSensor(sensor: any): Promise<void> {
    return this._snapshot.refreshSensor(sensor);
  }

  /** The host calls this once a refresh request is genuinely in flight. */
  markRefreshStarted(): void {
    this._snapshot.markRefreshStarted();
  }

  /** Completes the promise handed to the toolbar's refresh control (once). */
  settleRefresh(): void {
    this._snapshot.settleRefresh();
  }

  /** The vessel glyph as a data URL, cached in the module. */
  vesselIconUrl(isHistory: boolean, color?: string): string {
    return this._snapshot.vesselIconUrl(isHistory, color);
  }

  // ── Track mode: entitlements ────────────────────────────────────────────────
  // `spendRefusal` went with the lookup (phase 9): it was the full refusal
  // ladder for the LAST paid action still on this facade, and the identical
  // expression already lives on `VrSpendGateway.refuse`, which every feature
  // reaches through `ctx.spend`. One ladder, one wallet, one voice.

  // ── Track mode: selection — extracted to SelectionController (phase 4) ──────
  /** Selects the clicked vessel — local, free; gives the drawers a subject. */
  activateVessel(properties: any, marker: any): void {
    this._selection.activateVessel(properties, marker);
  }

  /** The map has been EMPTIED — the selection goes with it (a track keeps it). */
  dropSelectionWithMap(): void {
    this._selection.dropSelectionWithMap();
  }

  /** Clears the selection, its halo and any track drawn for it. */
  deactivateVessel(): void {
    this._selection.deactivateVessel();
  }

  // ── Track mode — extracted to TrackFeature + TrackLayers (phase 6) ──────────
  /** FREE replay of a track this task already paid for. Host + panel entry. */
  replaySavedTrack(trackId: string): Promise<void> {
    return this._trackFeature.replaySavedTrack(trackId);
  }

  /**
   * Syncs the local credit wallet after a successful (non-empty) paid call.
   * Prefers the server's authoritative balance; falls back to `current - cost`
   * only when the response omits it.
   */
  applyCreditSpend(res: any, sensorId: string | undefined, cost: number): void {
    // `!= null` before the finite check: Number(null) is 0, so an omitted or
    // null balance would otherwise read as "no credits left" — the same defect
    // that once reported a full refresh balance as exhausted.
    const reported = res?.apiCreditsRemaining;
    const authoritative = Number(reported);
    // Only the sensor that was charged — the server decrements exactly one.
    const sensor = (this.host.collection?.sensors ?? []).find(
      (s: any) =>
        s?.operatorKey === OPERATORS.VOYAGERADAR && (!sensorId || s?.id === sensorId)
    );
    if (!sensor) return;
    if (reported != null && isFinite(authoritative)) {
      sensor.apiCreditsRemaining = Math.max(0, authoritative);
    } else {
      const current = voyageCreditsRemaining(sensor);
      // NaN-safe: with no usable number there is nothing honest to write.
      const base = current != null ? current : Number(sensor.apiCredits);
      if (isFinite(base)) sensor.apiCreditsRemaining = Math.max(0, base - cost);
    }
    if (res?.track) {
      if (!Array.isArray(sensor.trackHistory)) sensor.trackHistory = [];
      sensor.trackHistory.push(res.track);
    }
    this.syncCreditLines();
    if (this.active) {
      this.vr.track.setSavedTracks(this._trackFeature.savedTracksFor(this.active.mmsi));
    }
  }

  // ── Located vessels (worldwide search) ──────────────────────────────────────
  // ── Vessel search — extracted to SearchFeature (phase 5) ────────────────────
  /** The free in-AOI index — kept callable here for `snapshotFixFor`. */
  searchLoaded(query: string, limit = 8): VesselSearchHit[] {
    return this._searchFeature.searchLoaded(query, limit);
  }

  /** Push the map facts the search drawer renders but cannot reach. */
  pushSearchContext(): void {
    this._searchFeature.pushSearchContext();
  }

  /** True when a history vessel's marker is currently drawn — the panel's "on map" tag. */
  isLocatedOnMap(mmsi: string): boolean {
    return this._locatedStore.isLocatedOnMap(mmsi);
  }

  /** True when the vessel has a saved record (drawn or not). */
  isSaved(mmsi: string): boolean {
    return this._locatedStore.isSaved(mmsi);
  }

  /** The card's context-menu for a hull, resolved from its state (I5). */
  popupOptsFor(mmsi: string): VoyagePopupActions {
    return this._popup.popupOptsFor(mmsi) as VoyagePopupActions;
  }

  /**
   * THIS PROVIDER'S card keys for one hull (D15) — every mounted feature's
   * contribution, merged, plus the lookup price, which belongs here rather
   * than in the shared host.
   *
   * `lookupCredits` used to be a named option on `PopupHostOptions`, which
   * meant shared core carried a field for one provider's paid lookup. The
   * lookup feature contributes no `popupActions()` of its own — it draws the
   * fresh fix rather than opening a drawer — so the price is added here, where
   * the other two prices already arrive.
   */
  private voyageCardActions(mmsi: string): VoyagePopupActions {
    const merged: VoyagePopupActions = { lookupCredits: this.lookupCredits };
    for (const f of this._features) Object.assign(merged, f.popupActions(mmsi));
    return merged;
  }

  /**
   * Route a card button to the controller that owns it (D15).
   *
   * The shared host reads `data-card-action` off the clicked element and hands
   * the string here without interpreting it; these four keys are this
   * provider's whole card vocabulary (`VoyageCardAction`). It used to be four
   * branches inside `popup.host.ts`, two of which reached `ctx.host.portCalls`
   * and `ctx.host.nearby` directly — shared core dispatching one provider's
   * features by name.
   *
   * The drawers OPEN AND FETCH every time, and take the MMSI from the card
   * rather than from the map's selection: four layers open this popup and only
   * the playback one calls `activateVessel`, so on a live snapshot hull there
   * is no active vessel to read — which is how the port-call button once came
   * to open an empty drawer and buy nothing.
   */
  private dispatchCardAction(action: string, mmsi: string): void {
    switch (action as VoyageCardAction) {
      case 'latest':
        this.refreshVesselLookup(mmsi, { draw: true });
        return;
      case 'ports':
        this.vr.portCalls.openAndFetch(mmsi, this.vesselNameFor(mmsi));
        return;
      case 'nearby':
        this.vr.nearby.openAndFetch(mmsi, this.vesselNameFor(mmsi));
        return;
      case 'poirow':
        this._poiFeature.pickPoiNearbyVessel(mmsi);
        return;
    }
  }

  /**
   * The best name we hold for a hull, or null.
   *
   * Only ever a title and a fallback: the port-call route prefers the
   * operator's own `shipName` off the first row it gets back, so this is what
   * the drawer shows until that lands. Ordered by how close each source is to
   * the vessel — the live selection, then a located record, then whatever the
   * drawn marker was labelled with.
   */
  vesselNameFor(mmsi: string): string | null {
    const m = String(mmsi ?? '').trim();
    if (!m) return null;
    if (this.active?.mmsi === m && this.active.name) return this.active.name;
    const located = this._locatedVessels.get(m)?.record;
    if (located?.name) return String(located.name);
    const props = this.liveMarkerFor(m)?.feature?.properties;
    const name = props?.['Name'] ?? props?.name;
    return name ? String(name) : null;
  }

  /** The popup's free "Save vessel" — the lookup feature's (phase 9). */
  saveSnapshotVessel(mmsi: string): void {
    this._lookupFeature.saveSnapshotVessel(mmsi);
  }

  /** MMSI of the by-MMSI lookup in flight, or null — the panel's spinner. */
  get lookupLoadingMmsi(): string | null {
    return this._lookupFeature.lookupLoadingMmsi;
  }

  /**
   * What one action costs on THIS sensor, off its live catalogue (an ee-admin
   * re-price sticks) with the shipped price as the fallback.
   *
   * Never a literal anywhere in this file: an ee-admin re-price has to move
   * every number the user sees. The getters below are named windows onto
   * this one call — none of them is shape dependent, because every endpoint
   * behind them answers by MMSI or is global, so an area task and a point task
   * reach them at the identical price. (The area/point REFRESH is the one that
   * does differ, and it is priced by `refreshCredits(sensor)`, which picks its
   * key from the tasked footprint.)
   *
   * `nearby` is the one that needs that stated rather than assumed, because it
   * is BOTH: it is the key `refreshActionKey(true)` returns, so on a point task
   * `nearbyCredits` and `refreshCredits(sensor)` resolve the same number by
   * construction — one catalogue entry per upstream endpoint, reached here by a
   * second gesture. Two prices could never drift apart because there is only
   * one, which is the property the shared key exists to buy.
   */
  private creditsFor(key: string): number {
    return actionCredits(this.sensor, key);
  }

  get lookupCredits(): number {
    return this.creditsFor('lookup');
  }

  // The port-call, nearby and search prices moved into their features
  // (phase 5) — same catalogue, reached through `SpendGate.priceFor`.

  /**
   * Push price + balance into every controller that prints a cost — EXTRACTED
   * to `ais/shared/map-core/spend-broadcast.ts` (phase 2), which carries the
   * full reasoning (why each controller is TOLD its prices, why the storage
   * scope rides along). The numbers come from `_spend` — Voyage Radar's
   * `SpendGate`. This delegate stays because ~10 call sites in this file push
   * after every wallet-moving response, and they should read as one act.
   */
  private syncCreditLines(): void {
    this._spendBroadcast.sync();
  }

  /**
   * A nearby ROW was clicked — place that hull on the map.
   *
   * Deliberately the same three-way decision `pickPoiNearbyVessel` already makes
   * for the POI list, because it is the same question asked from a different
   * drawer: already drawn, or kept with a stored fix, and it is free; otherwise
   * the 1-credit lookup, which is the ONLY way to get a position for a row whose
   * list carries none.
   *
   * `onSettled` is threaded back so the row's own spinner clears on both the
   * success and the failure path — and is fired IMMEDIATELY on the free paths,
   * which never had a request to wait for.
   */
  private pickNearbyRowVessel(mmsi: string, onSettled?: () => void): void {
    const m = String(mmsi ?? '').trim();
    if (!m) {
      onSettled?.();
      return;
    }
    const settleAndSync = () => {
      onSettled?.();
      // The badge is what tells the user a row is now free to revisit, and it
      // is read off the map rather than assumed from the click succeeding.
      this.vr.nearby.refreshOnMap((k) => this._locatedVessels.has(k));
    };

    if (this._locatedVessels.has(m)) {
      this.selectLocated(m, { openPopup: true, fly: true });
      settleAndSync();
      return;
    }
    const rec = (this.sensor?.vesselHistory ?? []).find((v: any) => String(v?.mmsi) === m);
    const stored = rec ? vrToLocatedRecord(rec) : null;
    if (stored && stored.lat != null && stored.lng != null) {
      this.locateHistoryRecord(rec);
      settleAndSync();
      return;
    }
    // Paid path. The row is marked busy by the controller only if a request
    // actually STARTED — the lookup turns several clicks back at the door — so a
    // row is never left spinning for a request that was never made.
    const started = this.refreshVesselLookup(m, {
      draw: true,
      select: true,
      onSettled: settleAndSync,
    });
    if (!started) settleAndSync();
  }

  /**
   * The fix for a hull the BROWSER drew, or null.
   *
   * Only the live-AOI snapshot case has a position the server does not: those
   * features live in the page and were never written to Mongo. A located or
   * searched hull is already in `vesselHistory` with an `asOf` block, and the
   * server reaches that itself and prefers it — so returning null here is not a
   * gap, it is deferring to the better source.
   */
  private snapshotFixFor(mmsi: string): { lat: number; lon: number } | null {
    const m = String(mmsi ?? '').trim();
    if (!m) return null;
    // `searchLoaded` is the free in-AOI index over the drawn snapshot and every
    // loaded frame — client-side and instant, no network and no credit, which is
    // the whole reason it is the right lookup for a value that is only ever a
    // fallback. Matched EXACTLY rather than taking the first hit: a digit query
    // matches on prefix there, and a neighbouring hull's position would be a
    // silently wrong centre rather than a visible failure.
    const hit = (this.searchLoaded(m, 8) || []).find((h) => h.mmsi === m);
    const lat = Number(hit?.lat);
    const lon = Number(hit?.lng);
    return Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null;
  }

  /**
   * A Vessel Search History row's Locate: draw its marker at the stored fix and
   * make it the selection. History is LIST-ONLY until this is clicked — the
   * deliberate opposite of the old hydrate-everything pass, so ticking a sensor
   * with fifty history rows does not carpet the chart in amber diamonds.
   */
  locateHistoryRecord(rec: any): void {
    const record = vrToLocatedRecord(rec);
    if (!record) return;
    record.kept = true;
    if (record.lat == null || record.lng == null) {
      this.host.popupService.openPopup(
        CollectionPopupComponent,
        'This record carries no stored position. Use Refresh to fetch the vessel\'s latest fix.',
        '',
        '40%'
      );
      return;
    }
    this.upsertLocatedMarker(record);
    this.host.map?.flyTo([record.lat, record.lng], Math.max(this.host.map?.getZoom?.() ?? 8, 8));
    this.selectLocated(record.mmsi, { openPopup: true });
  }

  /**
   * The by-MMSI lookup (1 credit) — the lookup feature's since phase 9. Kept
   * on the facade because the host component drives it (a Saved Vessels row's
   * Refresh) and the POI feature reaches it through its own injected option:
   * a feature never calls another feature (rule R3), so the facade is where
   * those two meet.
   *
   * Returns whether a request was actually SENT — several clicks are turned
   * back at the door, and a caller showing a spinner has to tell those apart.
   */
  refreshVesselLookup(
    mmsi: string,
    opts: { draw?: boolean; select?: boolean; onSettled?: () => void } = {},
  ): boolean {
    return this._lookupFeature.refreshVesselLookup(mmsi, opts);
  }

  // ── POI target — extracted to PoiNearbyFeature (phase 6) ────────────────────
  /** A POI refresh landed: fill the tasked target and open its nearby list. */
  showPoiNearby(scene: any, vessels: any[]): void {
    this._poiFeature.showPoiNearby(scene, vessels);
  }

  /** Takes the nearby RESULT off the map — the reticle and disc stay. */
  clearPoiNearby(): void {
    this._poiFeature.clearPoiNearby();
  }

  /**
   * A worldwide search result the user picked (A4 → A5): place it, keep it,
   * and — unless a trace is mid-flight — make it the selection.
   */
  locateSearchResult(result: any, query: string): boolean {
    const record = vrToLocatedRecord(result);
    if (!record) return false;
    if (record.lat == null || record.lng == null) {
      // The feed answered without a fix — nothing to place, and pretending
      // otherwise would drop a marker at [0,0].
      this.host.popupService.openPopup(
        CollectionPopupComponent,
        'The feed carries no position for this vessel right now, so it cannot be placed on the map.',
        '',
        '40%'
      );
      return false;
    }
    record.query = query;
    record.source = 'search';
    this.upsertLocatedMarker(record);
    this.keepVessel(record);
    this.host.map?.flyTo([record.lat, record.lng], Math.max(this.host.map?.getZoom?.() ?? 8, 8));
    this.selectLocated(record.mmsi, { openPopup: true });
    return true;
  }

  /** A row from the free in-AOI list: fly to it and select it where possible. */
  locateLoadedVessel(hit: VesselSearchHit): void {
    if (hit.source === 'live') {
      const marker = this.liveMarkerFor(hit.mmsi);
      if (marker) {
        const latlng = marker.getLatLng();
        this.host.map?.flyTo(latlng, Math.max(this.host.map?.getZoom?.() ?? 8, 8));
        this.activateVessel(marker?.feature?.properties ?? { MMSI: hit.mmsi, Name: hit.name }, marker);
        this.openVesselPopupAt(latlng, marker?.feature?.properties ?? {}, this.popupOptsFor(hit.mmsi));
        return;
      }
    }
    if (this._locatedVessels.has(hit.mmsi)) {
      this.selectLocated(hit.mmsi, { openPopup: true, fly: true });
      return;
    }
    // A history hit that is not drawn yet — the list-only rule means this is
    // the normal path for a kept vessel: locate it from its stored record.
    if (hit.source === 'kept') {
      const rec = (this.sensor?.vesselHistory ?? []).find(
        (v: any) => String(v?.mmsi) === hit.mmsi,
      );
      if (rec) {
        this.locateHistoryRecord(rec);
        return;
      }
    }
    // A frame-only hit: the hull was in an earlier loaded snapshot. Fly to
    // where that frame saw it; stepping playback there is the user's call.
    if (hit.lat != null && hit.lng != null) {
      this.host.map?.flyTo([hit.lat, hit.lng], Math.max(this.host.map?.getZoom?.() ?? 8, 8));
    }
  }

  /**
   * Selection guard (A2): locating never steals the selection out from under a
   * trace. `deactivateVessel` closes the drawer and clears the active vessel,
   * so an unguarded swap would silently retarget a trace someone was mid-way
   * through configuring — or discard one that is loading.
   */
  private canTakeSelection(): boolean {
    if (this.vr.track.snapshot().trackLoading) return false;
    if (this.vr.track.hasTrack) return false;
    if (this.host.toolbarDrawer.active !== null) return false;
    return true;
  }

  /** Make a located vessel the map selection (guard permitting) — the normal
   *  grammar: selection is what the ordinary Trace button acts on. */
  selectLocated(mmsi: string, opts: { openPopup?: boolean; fly?: boolean } = {}): void {
    const entry = this._locatedVessels.get(mmsi);
    if (!entry) return;
    const r = entry.record;
    if (opts.fly && r.lat != null && r.lng != null) {
      this.host.map?.flyTo([r.lat, r.lng], Math.max(this.host.map?.getZoom?.() ?? 8, 8));
    }
    if (this.canTakeSelection()) {
      this.deactivateVessel();
      this.active = {
        mmsi,
        name: r.name ?? mmsi,
        color: LOCATED_COLOR,
        lat: r.lat ?? 0,
        lng: r.lng ?? 0,
        timestampIso: r.timeUtc ?? '',
        shipType: r.shipType ?? 'OTHER',
      };
      this.syncCreditLines();
      this.vr.track.setSavedTracks(this._trackFeature.savedTracksFor(mmsi));
      this.vr.track.setActiveVessel(this.active);
    }
    if (opts.openPopup) this.openLocatedInfo(mmsi);
    this.pushLocatedState();
  }

  /** Persist a located vessel into `vesselHistory` — free, per §A5. */
  private keepVessel(record: LocatedVesselRecord): void {
    this._locatedStore.keepVessel(record);
  }

  /** Take a located vessel's marker off the map, keeping its saved record. */
  takeLocatedOffMap(mmsi: string): void {
    this._locatedStore.takeLocatedOffMap(mmsi);
  }

  /** Take EVERY located marker off the map in one act (records all kept). */
  takeAllLocatedOffMap(): void {
    this._locatedStore.takeAllLocatedOffMap();
  }

  /** Delete a vessel from Saved Vessels — permanently, and off the map with it. */
  removeLocatedVessel(mmsi: string): void {
    this._locatedStore.removeLocatedVessel(mmsi);
  }

  /** Located markers follow the snapshot-wide labels toggle (one Tt switch). */
  private syncLocatedLabels(): void {
    this._locatedLayer.syncLocatedLabels();
  }

  /** The drawer row's crosshair: recentre the map on a located vessel's fix. */
  centerLocatedVessel(mmsi: string): void {
    this._locatedLayer.centerLocatedVessel(mmsi);
  }

  /** The same read-only popup every hull gets, fed from the located record. */
  openLocatedInfo(mmsi: string): void {
    this._locatedLayer.openLocatedInfo(mmsi);
  }

  /** One place that opens the standard vessel popup at a point. */
  private openVesselPopupAt(latlng: any, properties: any, opts?: VoyagePopupActions): void {
    this._popup.openVesselPopupAt(latlng, properties, opts);
  }

  // `LOCATED_COLOR`, `isStaleFix` and the amber glyph rules all live pure in
  // `ais/shared/models/located.model.ts` (phases 1 + 4).

  // `toLocatedRecord` became `vrToLocatedRecord` in
  // `ais/voyage-radar/gateways/vr-lookup.gateway.ts` (phase 9). It maps Voyage
  // Radar's identity/asOf/details envelope, which phase 1 already refused to
  // call shared vocabulary — five callers across three features and this file
  // import it there, which is why it is a gateway function and not a method
  // on the feature that spends for it (rule R3).

  /** Create or update the amber marker for a located vessel. */
  private upsertLocatedMarker(record: LocatedVesselRecord): void {
    this._locatedLayer.upsertLocatedMarker(record);
  }

  // The amber glyph delegate went in phase 7 WITH the suite that needed it.
  // It existed for one caller — the located-glyph specs, which reached it
  // through `(controller as any)` — and those now call the pure rules in
  // `ais/shared/models/located.model.ts` directly. The lesson that put it here
  // survives in that spec's header: a cast hides a missing member from `tsc`,
  // so a spec written through one proves nothing about the seam it names.

  /** Recompute the search-history rows and hand them to their controller. */
  private pushLocatedState(): void {
    this._locatedStore.pushLocatedState();
  }

  /** Take every located artefact off the map (task switch — see reset()). */
  private clearLocatedVessels(): void {
    // Field-nulling only, on both halves — the shared sweep has already
    // cleared the map (invariant I1).
    this._locatedLayer.dropRefs();
    this._locatedStore.dropRefs();
    // Nulled rather than closed: a reference to the previous task's card must
    // not survive into the next one.
    this._popup.dropRefs();
    this.vr.locatedVessels.close();
  }

  // ── Teardown ────────────────────────────────────────────────────────────────
  /**
   * Resets everything this controller owns — both halves. Called from the host's
   * `_resetVoyageRadarToolbar`, which `resetMapNew` invokes on a task switch.
   *
   * Deliberately nulls the layer fields rather than removing the layers: the
   * shared reset has already swept the map, so a removeLayer() here would be
   * operating on a map that no longer holds them.
   */
  reset(): void {
    this.host.toolbarDrawer.close();
    // The search cache holds worldwide results bought against the OUTGOING
    // sensor's wallet, so it leaves with the task rather than answering the
    // next one's queries for free.
    this.vr.vesselSearch.reset();
    // Feature-owned state drops here — the POI result, and track mode's
    // layers, hover handlers and fleet dim. The three drawer features
    // deliberately override nothing: their in-flight guards were never reset
    // before either.
    //
    // THE FAN-OUT RUNS LATE — see below, next to where it actually sits. It
    // must not move up here, however tidy that would read.
    //
    // Off the map before the map is swept: this handler is the one thing here
    // that outlives the layers, and a task switch would otherwise leave it
    // walking a layer that no longer exists on every pan.
    this.stopViewportRepaint();
    this.clearVesselClusters();
    if (this.vr.playback.mode === 'playback') {
      this.vr.playback.close();
    }
    this.vr.track.close();
    // Drop the SELECTION, not just the track — otherwise the next collection
    // opens with a stale "Trace · <old vessel>" pill that does nothing.
    this.vr.track.setActiveVessel(null);
    this.active = null;

    // The cluster layer and the labels-painted flag — nulled on the display
    // module, never removed (I1): the sweep has already taken the group off
    // the map, and the next display pass builds a fresh one.
    this._display.dropRefs();
    // The playback layer, the hidden-live flag and the step cache — the
    // snapshot module's, nulled without touching the map (invariant I1).
    this._snapshot.dropRefs();
    this.releaseLiveLayer();

    // The halo ref — nulled on the selection module, never removed (I1).
    this._selection.dropRefs();

    // Feature-owned state drops HERE, and the position is load-bearing: it is
    // where the monolith nulled these same fields, after `track.close()`
    // rather than before it.
    //
    // `close()` LOOKS inert — it only clears the controller's own points — but
    // `_clearTrack` fires `onRenderTrack(null)` unconditionally, and a null
    // frame IS the layer teardown: `renderTrackFrame(null)` → `clearTrackLayers()`,
    // which restores the view the user had before the track took the map over.
    // That restore reads `_preTrackView`, so a fan-out placed above `close()`
    // blanks it first and the map silently stays parked on the track's fitted
    // bounds. (Regression found in review, 2026-08-12; `onExit` is the callback
    // `close()` does not fire, which is what made the early placement look safe.)
    //
    // Everything the features drop here is field-nulling plus `_detachTrackHover`
    // — nothing below reads any of it.
    for (const f of this._features) f.reset();

    // Located vessels belong to the task that just closed; the next task
    // hydrates its own from its own vesselHistory. Field-nulling only, like
    // every other layer here — the shared sweep has already cleared the map.
    this.clearLocatedVessels();

    // The target belongs to the task that just closed, so the references go
    // with it — the next task builds its own from its own footprint.
    // `dropRefs`, NOT `clearTarget`: the sweep this runs inside has already
    // taken the layer off the map (invariant I1), so the controller only nulls
    // its fields — the POI radius included, since the reticle that was
    // carrying it is gone. The POI result state goes with it.
    this._target.dropRefs();
    // The POI RESULT (rows, query, busy row) drops through the feature's own
    // `reset()` in the fan-out above — same fields, now feature-owned.
  }
}
